import { useState, useEffect } from 'react';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const AboutPageForm = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const { user } = useSelector((state) => state.user);
    const canEdit = user.role === "admin" || user.adminRole?.permissions?.pages?.edit;

    const [heroTitle, setHeroTitle] = useState("");
    const [heroTitleZh, setHeroTitleZh] = useState("");
    const [heroSubtitle, setHeroSubtitle] = useState("");
    const [heroSubtitleZh, setHeroSubtitleZh] = useState("");
    const [rebrandTitle, setRebrandTitle] = useState("");
    const [rebrandTitleZh, setRebrandTitleZh] = useState("");
    const [rebrandBodyHtml, setRebrandBodyHtml] = useState("");
    const [rebrandBodyHtmlZh, setRebrandBodyHtmlZh] = useState("");
    const [storyTitle, setStoryTitle] = useState("");
    const [storyTitleZh, setStoryTitleZh] = useState("");
    const [storyBodyHtml, setStoryBodyHtml] = useState("");
    const [storyBodyHtmlZh, setStoryBodyHtmlZh] = useState("");
    const [missionTitle, setMissionTitle] = useState("");
    const [missionTitleZh, setMissionTitleZh] = useState("");
    const [missionBodyHtml, setMissionBodyHtml] = useState("");
    const [missionBodyHtmlZh, setMissionBodyHtmlZh] = useState("");
    const [brands, setBrands] = useState([]);
    const [stores, setStores] = useState([]);

    useEffect(() => {
        const fetchPage = async () => {
            try {
                const { data } = await axios.get('/api/v1/about-page');
                const p = data.page;
                setHeroTitle(p.heroTitle); setHeroTitleZh(p.heroTitleZh || "");
                setHeroSubtitle(p.heroSubtitle); setHeroSubtitleZh(p.heroSubtitleZh || "");
                setRebrandTitle(p.rebrandTitle); setRebrandTitleZh(p.rebrandTitleZh || "");
                setRebrandBodyHtml(p.rebrandBodyHtml); setRebrandBodyHtmlZh(p.rebrandBodyHtmlZh || "");
                setStoryTitle(p.storyTitle); setStoryTitleZh(p.storyTitleZh || "");
                setStoryBodyHtml(p.storyBodyHtml); setStoryBodyHtmlZh(p.storyBodyHtmlZh || "");
                setMissionTitle(p.missionTitle); setMissionTitleZh(p.missionTitleZh || "");
                setMissionBodyHtml(p.missionBodyHtml); setMissionBodyHtmlZh(p.missionBodyHtmlZh || "");
                setBrands(p.brands || []);
                setStores(p.stores || []);
            } catch (error) {
                enqueueSnackbar("Failed to load About page", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchPage();
        // eslint-disable-next-line
    }, []);

    const updateBrand = (i, val) => setBrands((prev) => prev.map((b, idx) => idx === i ? val : b));
    const addBrand = () => setBrands((prev) => [...prev, ""]);
    const removeBrand = (i) => setBrands((prev) => prev.filter((_, idx) => idx !== i));

    const updateStore = (i, patch) => setStores((prev) => prev.map((s, idx) => idx === i ? { ...s, ...patch } : s));
    const addStore = () => setStores((prev) => [...prev, { name: "", address: "", addressZh: "", hours: "" }]);
    const removeStore = (i) => setStores((prev) => prev.filter((_, idx) => idx !== i));

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.put('/api/v1/admin/about-page', {
                heroTitle, heroTitleZh, heroSubtitle, heroSubtitleZh,
                rebrandTitle, rebrandTitleZh, rebrandBodyHtml, rebrandBodyHtmlZh,
                storyTitle, storyTitleZh, storyBodyHtml, storyBodyHtmlZh,
                missionTitle, missionTitleZh, missionBodyHtml, missionBodyHtmlZh,
                brands, stores,
            });
            enqueueSnackbar("About page updated", { variant: "success" });
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to save", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title="Admin: Edit About Page | 1313 Mart" />
            {saving && <BackdropLoader />}

            <div className="flex flex-col gap-5 bg-white rounded-lg shadow p-4 sm:p-6">
                <h1 className="text-lg font-medium uppercase">Edit About Us Page</h1>

                <div className="border rounded-lg p-4 flex flex-col gap-3">
                    <h2 className="font-medium">Hero</h2>
                    <TextField label="Hero Title" size="small" value={heroTitle} onChange={(e) => setHeroTitle(e.target.value)} />
                    <TextField label="Hero Title (中文 — optional)" size="small" value={heroTitleZh} onChange={(e) => setHeroTitleZh(e.target.value)} />
                    <TextField label="Hero Subtitle" size="small" multiline rows={2} value={heroSubtitle} onChange={(e) => setHeroSubtitle(e.target.value)} />
                    <TextField label="Hero Subtitle (中文 — optional)" size="small" multiline rows={2} value={heroSubtitleZh} onChange={(e) => setHeroSubtitleZh(e.target.value)} />
                </div>

                <div className="border rounded-lg p-4 flex flex-col gap-3">
                    <h2 className="font-medium">Rebrand Story Section</h2>
                    <TextField label="Section Title" size="small" value={rebrandTitle} onChange={(e) => setRebrandTitle(e.target.value)} />
                    <TextField label="Section Title (中文 — optional)" size="small" value={rebrandTitleZh} onChange={(e) => setRebrandTitleZh(e.target.value)} />
                    <ReactQuill theme="snow" value={rebrandBodyHtml} onChange={setRebrandBodyHtml} />
                    <p className="text-xs text-gray-500 mt-1">中文 — optional</p>
                    <ReactQuill theme="snow" value={rebrandBodyHtmlZh} onChange={setRebrandBodyHtmlZh} />
                </div>

                <div className="border rounded-lg p-4 flex flex-col gap-3">
                    <h2 className="font-medium">Our Story Section</h2>
                    <TextField label="Section Title" size="small" value={storyTitle} onChange={(e) => setStoryTitle(e.target.value)} />
                    <TextField label="Section Title (中文 — optional)" size="small" value={storyTitleZh} onChange={(e) => setStoryTitleZh(e.target.value)} />
                    <ReactQuill theme="snow" value={storyBodyHtml} onChange={setStoryBodyHtml} />
                    <p className="text-xs text-gray-500 mt-1">中文 — optional</p>
                    <ReactQuill theme="snow" value={storyBodyHtmlZh} onChange={setStoryBodyHtmlZh} />
                </div>

                <div className="border rounded-lg p-4 flex flex-col gap-3">
                    <h2 className="font-medium">Our Mission Section</h2>
                    <TextField label="Section Title" size="small" value={missionTitle} onChange={(e) => setMissionTitle(e.target.value)} />
                    <TextField label="Section Title (中文 — optional)" size="small" value={missionTitleZh} onChange={(e) => setMissionTitleZh(e.target.value)} />
                    <ReactQuill theme="snow" value={missionBodyHtml} onChange={setMissionBodyHtml} />
                    <p className="text-xs text-gray-500 mt-1">中文 — optional</p>
                    <ReactQuill theme="snow" value={missionBodyHtmlZh} onChange={setMissionBodyHtmlZh} />
                </div>

                <div className="border rounded-lg p-4 flex flex-col gap-3">
                    <h2 className="font-medium">Brands We Distribute</h2>
                    {brands.map((b, i) => (
                        <div key={i} className="flex items-center gap-2">
                            <TextField size="small" fullWidth value={b} onChange={(e) => updateBrand(i, e.target.value)} />
                            <IconButton color="error" onClick={() => removeBrand(i)}><DeleteIcon fontSize="small" /></IconButton>
                        </div>
                    ))}
                    <button type="button" onClick={addBrand} className="flex items-center gap-1 text-primary-blue text-sm w-fit"><AddIcon fontSize="small" /> Add Brand</button>
                </div>

                <div className="border rounded-lg p-4 flex flex-col gap-4">
                    <h2 className="font-medium">Our Stores</h2>
                    {stores.map((s, i) => (
                        <div key={i} className="border rounded p-3 flex flex-col gap-2 bg-gray-50">
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-600">Store #{i + 1}</span>
                                <IconButton color="error" size="small" onClick={() => removeStore(i)}><DeleteIcon fontSize="small" /></IconButton>
                            </div>
                            <TextField label="Name" size="small" value={s.name} onChange={(e) => updateStore(i, { name: e.target.value })} />
                            <TextField label="Address (English)" size="small" value={s.address} onChange={(e) => updateStore(i, { address: e.target.value })} />
                            <TextField label="Address (Chinese, optional)" size="small" value={s.addressZh} onChange={(e) => updateStore(i, { addressZh: e.target.value })} />
                            <TextField label="Hours" size="small" value={s.hours} onChange={(e) => updateStore(i, { hours: e.target.value })} />
                        </div>
                    ))}
                    <button type="button" onClick={addStore} className="flex items-center gap-1 text-primary-blue text-sm w-fit"><AddIcon fontSize="small" /> Add Store</button>
                </div>

                {canEdit && (
                    <div className="flex justify-end">
                        <button onClick={handleSave} className="bg-primary-orange uppercase px-6 py-3 text-white font-medium rounded shadow hover:shadow-lg">Save Changes</button>
                    </div>
                )}
            </div>
        </>
    );
};

export default AboutPageForm;
